<template>
  <div :class="`flex ${isNotActivation ? 'justify-between gap-1' : 'justify-evenly'}`">
    <div v-if="isNotActivation">
      <el-radio-group v-model="numberType" @change="changeNumberType">
        <el-radio v-for="option in numberTypes" :key="option.value" :label="option.value">
          {{ option.label }}
        </el-radio>
      </el-radio-group>
    </div>
    <div
      :class="[
        { 'el-input-number': isShowControlButton },
        { 'is-disabled': disabled },
        { 'is-without-controls': false },
        { 'is-controls-right': false },
      ]"
      @dragstart.prevent
    >
      <span
        v-if="isShowControlButton"
        v-repeat-click="decreaseVal"
        class="el-input-number__decrease"
        role="button"
        :class="{ 'is-disabled': isDisableMinButton }"
        @keydown.enter="decreaseVal"
      >
        <i :class="`el-icon-${false ? 'arrow-down' : 'minus'}`"></i>
      </span>
      <span
        v-if="isShowControlButton"
        v-repeat-click="increaseVal"
        class="el-input-number__increase"
        role="button"
        :class="{ 'is-disabled': isDisableMaxButton }"
        @keydown.enter="increaseVal"
      >
        <i :class="`el-icon-${false ? 'arrow-up' : 'plus'}`"></i>
      </span>
      <el-input
        @blur="onBlur"
        :class="`custom-input-number-tf`"
        :min="isNotActivation ? 0 : minValue"
        :max="maxValue"
        v-model="displayValue"
        :disabled="disabled"
        :placeholder="placeholder"
        type="tel"
        inputmode="numeric"
      >
        <template v-if="isPrefix" #prefix>
          {{ unitType }}
        </template>
        <template v-if="isSuffix" #suffix>
          {{ unitType }}
        </template>
      </el-input>
    </div>
    <InputReadersSuffix
      customClass="flex"
      :fieldName="name"
      :bcrType="barcodeReaderType"
      :ocrType="ocrReaderType"
      v-model="displayValue"
      @update:result="onInputReaderChanged"
    />
  </div>
</template>

<script lang="ts">
import OCRComponent from '@/components/OCRComponent.vue'
import IconBarcodeReader from '@/components/svg/IconBarcodeReader.vue'
import IconOCR from '@/components/svg/IconOCR.vue'
import IconSave from '@/components/svg/IconSave.vue'
import { ENumberType } from '@/utils/enums'
import { RepeatClick } from 'element-plus/lib/directives'
import { maska } from 'maska'
import { EBCRType, ECustomFieldUnitPosition, EOCRType } from 'smartbarcode-web-core/src/utils/enums/index'
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import InputReadersSuffix from './InputReadersSuffix.vue'
@Options({
  directives: { maska, RepeatClick },
  emits: ['update:modelValue', 'change'],
  components: {
    OCRComponent,
    IconOCR,
    IconBarcodeReader,
    IconSave,
    InputReadersSuffix,
  },
  name: 'Adjustment',
})
export default class Adjustment extends Vue {
  @Prop({ type: [Number, String] }) modelValue?: number
  @Prop({ type: Number, default: 0 }) readonly minValue?: number
  @Prop({ type: Number }) readonly maxValue?: number
  @Prop({ type: Number }) readonly precision?: number
  @Prop({ type: String }) readonly fieldName?: string = ''
  @Prop({ type: String }) readonly name?: string = ''
  @Prop({ type: String }) readonly placeholder?: string = ''
  @Prop({ type: Boolean }) readonly disabled?: boolean = false
  @Prop({ type: Boolean, default: true }) readonly isShowControlButton?: boolean
  @Prop({ default: EBCRType.UNKNOWN }) barcodeReaderType!: EBCRType
  @Prop({ default: EOCRType.UNKNOWN }) ocrReaderType!: EOCRType
  @Prop({ type: String }) readonly unitType?: string
  @Prop({ default: ECustomFieldUnitPosition.UNKNOWN }) readonly position?: ECustomFieldUnitPosition

  isShowDialog = false

  displayValue = ''
  numberType = '' as ENumberType

  mounted() {
    const initValue = this.modelValue || this.minValue
    if (initValue) {
      this.displayValue = initValue.toString()
      this.formatValue()
    }
    if (!this.isNotActivation) {
      this.numberType = ENumberType.INCREASE
    }
  }

  get isNotActivation() {
    return !this.$route.path.endsWith('activate/package') && !this.$route.path.endsWith('/edit')
  }

  get numberTypes() {
    return [
      { label: this.$t(ENumberType.INCREASE), value: ENumberType.INCREASE },
      { label: this.$t(ENumberType.DECREASE), value: ENumberType.DECREASE },
    ]
  }

  get isDisableMinButton() {
    return this.minValue && this.minValue === this.modelValue
  }

  get isDisableMaxButton() {
    return this.maxValue && this.maxValue === this.modelValue
  }

  get isSuffix() {
    return this.position === ECustomFieldUnitPosition.SUFFIX
  }

  get isPrefix() {
    return this.position === ECustomFieldUnitPosition.PREFIX
  }

  changeNumberType() {
    this.formatValue()
  }

  numberToString(num: number) {
    return Number(num.toFixed(this.precisionNum)).toLocaleString()
  }

  stringToNumber(str: string): number {
    return Number(Number(str.replace(/,/g, '')).toFixed(this.precisionNum))
  }

  increaseVal() {
    let formattedVal = this.stringToNumber(this.displayValue || '0') + 1
    if (this.maxValue && formattedVal > this.maxValue) {
      formattedVal = this.maxValue
    }
    this.displayValue = formattedVal.toString()
    this.formatValue()
  }

  decreaseVal() {
    let formattedVal = this.stringToNumber(this.displayValue || '0') - 1
    if (this.isNotActivation) {
      if (formattedVal < 0) {
        formattedVal = 0
      }
    } else {
      if (this.minValue && formattedVal < this.minValue) {
        formattedVal = this.minValue
      }
    }
    this.displayValue = formattedVal.toString()
    this.formatValue()
  }

  onBlur(event: FocusEvent) {
    this.formatValue()
  }

  onInputReaderChanged() {
    this.formatValue()
  }

  formatValue() {
    if (!this.displayValue) {
      this.updateModelVal()
      return
    }
    let formattedVal = this.stringToNumber(this.displayValue)
    if (this.numberType === ENumberType.DECREASE) {
      formattedVal = -formattedVal
    }
    if (this.minValue) {
      if (formattedVal < this.minValue) {
        formattedVal = this.minValue
      }
    }
    if (this.maxValue) {
      if (formattedVal > this.maxValue) {
        formattedVal = this.maxValue
      }
    }
    if (!this.isNotActivation) {
      this.displayValue = formattedVal.toString()
    }
    this.updateModelVal(formattedVal)
    return formattedVal
  }

  get precisionNum() {
    return this.precision ?? 1
  }

  updateModelVal(val?: number) {
    this.$emit('update:modelValue', val)
  }

  @Watch('modelValue')
  onModelChanged() {
    // Update the value When modelValue change from the out side.
    if (this.modelValue) {
      this.formatValue()
    } else {
      this.displayValue = '0'
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~@/assets/css/theme.scss';
.el-radio-group {
  width: min-content;
  &:deep() {
    .el-radio {
      margin-right: 0;
    }
  }
}
.el-input-number:deep() {
  .el-input--suffix {
    .el-input__inner {
      padding-right: 70px;
    }
  }
  .el-input--prefix {
    .el-input__inner {
      padding-left: 70px;
    }
  }
}
.custom-input-number-tf:deep() {
  .el-input {
    &__suffix {
      position: absolute;
      right: 48px;
    }
  }
  .el-input {
    &__prefix {
      position: absolute;
      left: 48px;
    }
  }
}
.el-input-number {
  width: 230px;
}
.custom-input-number {
  display: flex;

  &-tf {
    text-align-last: center;
  }

  &-btn {
    margin-top: 1px;
    width: 54px;
    height: 40px;
    border-radius: 0px !important;
  }
}

.half-width {
  width: 40%;
}

.align-middle {
  vertical-align: middle;
}

.field-input {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 8px 0px;

  &-reader {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    margin: 8px 0px;
  }

  &.error .el-input {
    border-color: $error;
  }

  .error-msg {
    color: $error;
    line-height: 1.2;
  }
}

el-dialog {
  height: 100vh;
  width: 100%;
}
</style>
